// Captures console.*, uncaught errors, unhandled promise rejections, and
// failed fetch requests into a ring buffer so customers can attach them to
// support tickets. Install once, as early as possible in main.js.

const MAX_ENTRIES = 1500
const PERSIST_TAIL = 400
const STORAGE_KEY = 'consoleLogCapture.tail'
const PERSIST_INTERVAL_MS = 2000

const buffer = []
let installed = false
let silent = false
let originals = null
let dirty = false
let persistTimer = null

function safeStringify(v) {
  if (v === null) return 'null'
  if (v === undefined) return 'undefined'
  const t = typeof v
  if (t === 'string') return v
  if (t === 'number' || t === 'boolean') return String(v)
  if (t === 'bigint') return String(v) + 'n'
  if (t === 'function') return '[Function ' + (v.name || 'anonymous') + ']'
  if (v instanceof Error) return v.stack || (v.name + ': ' + v.message)
  try {
    const seen = new WeakSet()
    return JSON.stringify(v, (k, val) => {
      if (val && typeof val === 'object') {
        if (seen.has(val)) return '[Circular]'
        seen.add(val)
        if (val instanceof Error) return val.stack || (val.name + ': ' + val.message)
      }
      if (typeof val === 'bigint') return String(val) + 'n'
      if (typeof val === 'function') return '[Function ' + (val.name || 'anonymous') + ']'
      return val
    })
  } catch (e) {
    try { return String(v) } catch (_) { return '[Unserializable]' }
  }
}

function formatArgs(args) {
  const parts = []
  for (let i = 0; i < args.length; i++) parts.push(safeStringify(args[i]))
  return parts.join(' ')
}

function push(level, args) {
  let msg
  try { msg = formatArgs(args) } catch (_) { msg = '[capture failed]' }
  buffer.push({ t: Date.now(), level, msg })
  if (buffer.length > MAX_ENTRIES) {
    buffer.splice(0, buffer.length - MAX_ENTRIES)
  }
  dirty = true
}

function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return
    for (const e of arr) {
      if (e && typeof e.t === 'number' && typeof e.msg === 'string') buffer.push(e)
    }
    if (buffer.length) {
      buffer.push({ t: Date.now(), level: 'info', msg: '--- new session ---' })
    }
  } catch (_) { /* ignore */ }
}

function flushPersist() {
  if (!dirty) return
  dirty = false
  try {
    const tail = buffer.length > PERSIST_TAIL ? buffer.slice(-PERSIST_TAIL) : buffer
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tail))
  } catch (_) { /* storage full or unavailable */ }
}

function patchConsole() {
  originals = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
    debug: console.debug,
  }
  console.log = function (...args) { push('log', args); if (!silent) try { originals.log.apply(console, args) } catch (_) { /* ignore */ } }
  console.info = function (...args) { push('info', args); if (!silent) try { originals.info.apply(console, args) } catch (_) { /* ignore */ } }
  console.debug = function (...args) { push('debug', args); if (!silent) try { originals.debug.apply(console, args) } catch (_) { /* ignore */ } }
  // Always pass warn/error through, regardless of silent — these are diagnostic.
  console.warn = function (...args) { push('warn', args); try { originals.warn.apply(console, args) } catch (_) { /* ignore */ } }
  console.error = function (...args) { push('error', args); try { originals.error.apply(console, args) } catch (_) { /* ignore */ } }
}

function attachErrorHandlers() {
  window.addEventListener('error', function (event) {
    const e = event && event.error
    const where = (event && event.filename) ? (event.filename + ':' + event.lineno + ':' + event.colno) : ''
    const stack = e && e.stack ? '\n' + e.stack : ''
    push('uncaught', ['window.error:', (event && event.message) || (e && e.message) || '', where, stack])
  })
  window.addEventListener('unhandledrejection', function (event) {
    const r = event && event.reason
    let msg
    if (r && r.stack) msg = r.stack
    else if (r && r.message) msg = r.message
    else msg = safeStringify(r)
    push('unhandledrejection', ['Unhandled rejection:', msg])
  })
}

function patchFetch() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return
  const orig = window.fetch.bind(window)
  window.fetch = function (...args) {
    const start = Date.now()
    let url = ''
    let method = 'GET'
    try {
      const req = args[0]
      if (typeof req === 'string') url = req
      else if (req && req.url) url = req.url
      const init = args[1]
      if (init && init.method) method = init.method
      else if (req && req.method) method = req.method
    } catch (_) { /* ignore */ }
    return orig(...args).then(function (res) {
      if (res && !res.ok) {
        push('network', ['HTTP', res.status, method, url, '(' + (Date.now() - start) + 'ms)'])
      }
      return res
    }, function (err) {
      push('network', ['fetch error', method, url, '(' + (Date.now() - start) + 'ms):', (err && err.message) || err])
      throw err
    })
  }
}

function startPersistTimer() {
  if (persistTimer) return
  persistTimer = setInterval(flushPersist, PERSIST_INTERVAL_MS)
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', flushPersist)
    window.addEventListener('pagehide', flushPersist)
  }
}

export default {
  install(opts = {}) {
    if (installed) return
    installed = true
    silent = !!opts.silent
    loadPersisted()
    patchConsole()
    attachErrorHandlers()
    patchFetch()
    startPersistTimer()
    push('info', ['ConsoleLogCapture installed (silent=' + silent + ')'])
  },
  setSilent(s) { silent = !!s },
  flush() { flushPersist() },
  clear() {
    buffer.length = 0
    dirty = true
    try { localStorage.removeItem(STORAGE_KEY) } catch (_) { /* ignore */ }
  },
  count() { return buffer.length },
  getFormattedLogs(meta = {}) {
    const lines = []
    const now = new Date().toISOString()
    lines.push('=== Web App Logs ===')
    lines.push('Generated: ' + now)
    if (typeof navigator !== 'undefined') lines.push('User-Agent: ' + navigator.userAgent)
    if (typeof window !== 'undefined') lines.push('URL: ' + window.location.href)
    if (meta.product) lines.push('Product: ' + meta.product)
    if (meta.version) lines.push('Version: ' + meta.version)
    if (meta.email) lines.push('Email: ' + meta.email)
    if (meta.altAccountId) lines.push('AccountId: ' + meta.altAccountId)
    if (meta.deviceId) lines.push('DeviceId: ' + meta.deviceId)
    lines.push('Entries: ' + buffer.length)
    lines.push('====================')
    lines.push('')
    for (let i = 0; i < buffer.length; i++) {
      const e = buffer[i]
      const ts = new Date(e.t).toISOString()
      lines.push('[' + ts + '] [' + e.level + '] ' + e.msg)
    }
    return lines.join('\n')
  }
}
